import { supabase } from "./supabase";
import { TZ } from "./format";
import { dayRange, fijiToday, shiftDay } from "./history";

export type ReportType = "daily" | "trips" | "stops" | "speeding";
export const REPORT_LABEL: Record<ReportType, string> = {
  daily: "Daily summary", trips: "Trips", stops: "Stops", speeding: "Speeding",
};
export const REPORT_MAX_DAYS: Record<ReportType, number> = { daily: 31, trips: 31, stops: 31, speeding: 7 };

export type ReportResult = {
  type: ReportType; from: string; to: string; limit?: number;
  devices: { id: string; name: string }[];
  rows: Record<string, any>[];
  totals: Record<string, number>;
  /** Daily report with one object and one day: that day's trips (same shape as trips rows). */
  dayTrips?: Record<string, any>[];
};

/** Average moving speed in km/h, 1 decimal (0 when nothing was driven). */
export const avgMoving = (distanceKm: number, drivingMin: number) => (drivingMin > 0 ? Math.round((distanceKm / (drivingMin / 60)) * 10) / 10 : 0);

export async function runReport(type: ReportType, deviceIds: string[], fromDay: string, toDay: string, limit?: number) {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Please sign in again.");
  const qs = new URLSearchParams({
    type, devices: deviceIds.join(","), from: dayRange(fromDay).from, to: dayRange(toDay).to,
    ...(limit ? { limit: String(limit) } : {}),
  });
  const r = await fetch(`/api/reports?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body?.error ?? `Report failed (${r.status})`);
  return body as ReportResult;
}

// ---------------------------------------------------------------- periods (Fiji days)
export type Preset = "today" | "yesterday" | "last7" | "thisMonth" | "lastMonth" | "custom";
export const PRESET_LABEL: Record<Preset, string> = {
  today: "Today", yesterday: "Yesterday", last7: "Last 7 days", thisMonth: "This month", lastMonth: "Last month", custom: "Custom",
};
export function presetRange(p: Preset): [string, string] {
  const t = fijiToday();
  const [y, m] = t.split("-").map(Number);
  const pad = (n: number) => String(n).padStart(2, "0");
  switch (p) {
    case "today": return [t, t];
    case "yesterday": { const d = shiftDay(t, -1); return [d, d]; }
    case "last7": return [shiftDay(t, -6), t];
    case "thisMonth": return [`${y}-${pad(m)}-01`, t];
    case "lastMonth": {
      const ly = m === 1 ? y - 1 : y, lm = m === 1 ? 12 : m - 1;
      const first = `${ly}-${pad(lm)}-01`;
      return [first, shiftDay(`${y}-${pad(m)}-01`, -1)];
    }
    default: return [t, t];
  }
}
export const daysBetween = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 86400000) + 1;

// ---------------------------------------------------------------- formatting + CSV
const dtf = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
/** "2026-09-28 11:23" in Fiji time */
export const fijiDateTime = (iso: string) => dtf.format(new Date(iso)).replace(",", "");
export const fmtMin = (min: number) => (min < 60 ? `${min} min` : `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, "0")} min`);
export const fmtSec = (s: number) => (s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`);
export const coord = (lat: number, lng: number) => `${lat.toFixed(5)}, ${lng.toFixed(5)}`;

export type Column = { key: string; label: string; num?: boolean; value: (r: Record<string, any>) => string | number };

export const COLUMNS: Record<ReportType, Column[]> = {
  daily: [
    { key: "date", label: "Date", value: (r) => r.date },
    { key: "device", label: "Object", value: (r) => r.device },
    { key: "distanceKm", label: "Distance (km)", num: true, value: (r) => r.distanceKm },
    { key: "trips", label: "Trips", num: true, value: (r) => r.trips },
    { key: "drivingMin", label: "Driving", num: true, value: (r) => fmtMin(r.drivingMin) },
    { key: "maxKmh", label: "Max km/h", num: true, value: (r) => r.maxKmh },
    { key: "avgKmh", label: "Avg km/h", num: true, value: (r) => r.avgKmh },
  ],
  trips: [
    { key: "device", label: "Object", value: (r) => r.device },
    { key: "start", label: "Start", value: (r) => fijiDateTime(r.start) },
    { key: "end", label: "End", value: (r) => fijiDateTime(r.end) },
    { key: "durationMin", label: "Duration", num: true, value: (r) => fmtMin(r.durationMin) },
    { key: "distanceKm", label: "Distance (km)", num: true, value: (r) => r.distanceKm },
    { key: "maxKmh", label: "Max km/h", num: true, value: (r) => r.maxKmh },
    { key: "avgKmh", label: "Avg km/h", num: true, value: (r) => r.avgKmh },
    { key: "from", label: "From", value: (r) => r.startAddress ?? coord(r.startLatLng[0], r.startLatLng[1]) },
    { key: "to", label: "To", value: (r) => r.endAddress ?? coord(r.endLatLng[0], r.endLatLng[1]) },
  ],
  stops: [
    { key: "device", label: "Object", value: (r) => r.device },
    { key: "start", label: "Arrived", value: (r) => fijiDateTime(r.start) },
    { key: "end", label: "Left", value: (r) => fijiDateTime(r.end) },
    { key: "durationMin", label: "Stopped for", num: true, value: (r) => fmtMin(r.durationMin) },
    { key: "place", label: "Location", value: (r) => r.address ?? coord(r.lat, r.lng) },
  ],
  speeding: [
    { key: "device", label: "Object", value: (r) => r.device },
    { key: "start", label: "Started", value: (r) => fijiDateTime(r.start) },
    { key: "durationSec", label: "Duration", num: true, value: (r) => fmtSec(r.durationSec) },
    { key: "maxKmh", label: "Top speed (km/h)", num: true, value: (r) => r.maxKmh },
    { key: "place", label: "Where (top speed)", value: (r) => coord(r.lat, r.lng) },
  ],
};

export function toCsv(type: ReportType, rows: Record<string, any>[]) {
  const cols = COLUMNS[type];
  const esc = (v: unknown) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  // CSV keeps raw minutes/seconds so spreadsheets can add them up
  const raw = (c: Column, r: Record<string, any>) =>
    c.key === "durationMin" || c.key === "drivingMin" ? r[c.key] : c.key === "durationSec" ? r.durationSec : c.value(r);
  const head = cols.map((c) => (c.key === "durationMin" || c.key === "drivingMin" ? `${c.label} (min)` : c.key === "durationSec" ? `${c.label} (s)` : c.label));
  return "﻿" + [head, ...rows.map((r) => cols.map((c) => raw(c, r)))].map((line) => line.map(esc).join(",")).join("\r\n");
}

export function download(name: string, text: string, mime = "text/csv;charset=utf-8") {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
